import React, { useState } from 'react';

function EmiCalculator() {
    const [principal, setPrincipal] = useState('');
    const [rate, setRate] = useState('');
    const [tenure, setTenure] = useState('');
    const [emi, setEmi] = useState(null);
    const [totalPayment, setTotalPayment] = useState(null);
    const [totalInterest, setTotalInterest] = useState(null);
    const [principalPercent, setPrincipalPercent] = useState(0);

    const calculateEMI = (e) => {
        e.preventDefault();
        const P = parseFloat(principal);
        const annualRate = parseFloat(rate);
        const n = parseInt(tenure);
        if (!P || !annualRate || !n) return;

        const r = annualRate / 12 / 100;
        const emiValue = (P * r * Math.pow(1 + r, n)) / (Math.pow(1 + r, n) - 1);
        const totalPay = emiValue * n;
        const totalInt = totalPay - P;
        const pPercent = Math.round((P / totalPay) * 100);

        setEmi(emiValue.toFixed(2));
        setTotalPayment(totalPay.toFixed(2));
        setTotalInterest(totalInt.toFixed(2));
        setPrincipalPercent(pPercent);
    };

    const handleReset = () => {
        setPrincipal('');
        setRate('');
        setTenure('');
        setEmi(null);
        setTotalPayment(null);
        setTotalInterest(null);
        setPrincipalPercent(0);
    };

    const formatINR = (val) =>
        Number(val).toLocaleString('en-IN', { maximumFractionDigits: 2 });

    return (
        <>
            <style>{`
                .emi-section {
                    background: #f4f6fb;
                    padding: 80px 0;
                }

                .emi-section .section-title h2 {
                    color: #0d1b4b;
                }

                .emi-card {
                    background: #ffffff;
                    border-radius: 16px;
                    padding: 40px;
                    box-shadow: 0 6px 30px rgba(13, 27, 75, 0.1);
                }

                .emi-label {
                    display: block;
                    margin-bottom: 8px;
                    font-weight: 700;
                    font-size: 13px;
                    color: #0d1b4b;
                    letter-spacing: 0.5px;
                    text-transform: uppercase;
                }

                .emi-input {
                    width: 100%;
                    padding: 13px 16px;
                    border: 2px solid #e4e8f0;
                    border-radius: 8px;
                    font-size: 15px;
                    color: #0d1b4b;
                    background: #f9fafc;
                    outline: none;
                    transition: border-color 0.2s, box-shadow 0.2s;
                    font-weight: 500;
                }

                .emi-input:focus {
                    border-color: #7b5ea7;
                    box-shadow: 0 0 0 3px rgba(123, 94, 167, 0.12);
                    background: #fff;
                }

                .emi-input::placeholder {
                    color: #b0b8cc;
                    font-weight: 400;
                }

                .emi-calc-btn {
                    width: 100%;
                    padding: 15px;
                    background: #7b5ea7;
                    color: #fff;
                    border: none;
                    border-radius: 8px;
                    font-size: 15px;
                    font-weight: 700;
                    letter-spacing: 1px;
                    text-transform: uppercase;
                    cursor: pointer;
                    transition: background 0.2s, transform 0.1s;
                }

                .emi-calc-btn:hover {
                    background: #6a4e94;
                    transform: translateY(-1px);
                }

                .emi-reset-btn {
                    width: 100%;
                    padding: 15px;
                    background: transparent;
                    color: #7b5ea7;
                    border: 2px solid #7b5ea7;
                    border-radius: 8px;
                    font-size: 15px;
                    font-weight: 700;
                    letter-spacing: 1px;
                    text-transform: uppercase;
                    cursor: pointer;
                    transition: all 0.2s;
                    margin-top: 10px;
                }

                .emi-reset-btn:hover {
                    background: #7b5ea7;
                    color: #fff;
                }

                .emi-result-header {
                    background: linear-gradient(135deg, #0d1b4b 0%, #1a3080 100%);
                    border-radius: 12px;
                    padding: 28px 30px;
                    text-align: center;
                    margin-bottom: 20px;
                    position: relative;
                    overflow: hidden;
                }

                .emi-result-header::before {
                    content: '';
                    position: absolute;
                    top: -40px;
                    right: -40px;
                    width: 120px;
                    height: 120px;
                    background: rgba(123, 94, 167, 0.3);
                    border-radius: 50%;
                }

                .emi-result-header::after {
                    content: '';
                    position: absolute;
                    bottom: -30px;
                    left: -20px;
                    width: 90px;
                    height: 90px;
                    background: rgba(255,255,255,0.06);
                    border-radius: 50%;
                }

                .emi-result-label {
                    color: rgba(255,255,255,0.7);
                    font-size: 13px;
                    font-weight: 600;
                    letter-spacing: 1.5px;
                    text-transform: uppercase;
                    margin-bottom: 8px;
                }

                .emi-result-value {
                    color: #fff;
                    font-size: 42px;
                    font-weight: 800;
                    letter-spacing: -1px;
                    margin: 0;
                }

                .emi-result-sub {
                    color: rgba(255,255,255,0.6);
                    font-size: 13px;
                    margin-top: 6px;
                }

                .emi-breakdown-row {
                    display: flex;
                    gap: 14px;
                    margin-bottom: 14px;
                }

                .emi-breakdown-card {
                    flex: 1;
                    border-radius: 10px;
                    padding: 18px 16px;
                }

                .emi-breakdown-card.principal {
                    background: #eef6ff;
                    border-left: 4px solid #2563eb;
                }

                .emi-breakdown-card.interest {
                    background: #fff4ec;
                    border-left: 4px solid #f97316;
                }

                .emi-breakdown-label {
                    font-size: 11px;
                    font-weight: 700;
                    text-transform: uppercase;
                    letter-spacing: 0.8px;
                    color: #888;
                    margin-bottom: 5px;
                }

                .emi-breakdown-amount {
                    font-size: 18px;
                    font-weight: 800;
                    margin: 0;
                }

                .emi-breakdown-amount.principal { color: #2563eb; }
                .emi-breakdown-amount.interest { color: #f97316; }

                .emi-total-row {
                    background: #f0ecf8;
                    border-radius: 10px;
                    padding: 18px 20px;
                    display: flex;
                    justify-content: space-between;
                    align-items: center;
                    border-left: 4px solid #7b5ea7;
                    margin-bottom: 20px;
                }

                .emi-total-label {
                    font-size: 13px;
                    font-weight: 700;
                    color: #555;
                    text-transform: uppercase;
                    letter-spacing: 0.5px;
                }

                .emi-total-amount {
                    font-size: 22px;
                    font-weight: 800;
                    color: #7b5ea7;
                    margin: 0;
                }

                /* Progress bar */
                .emi-progress-wrap {
                    margin-top: 4px;
                }

                .emi-progress-label {
                    display: flex;
                    justify-content: space-between;
                    font-size: 12px;
                    font-weight: 600;
                    color: #888;
                    margin-bottom: 6px;
                }

                .emi-progress-bar {
                    height: 8px;
                    border-radius: 99px;
                    background: #ffe0cc;
                    overflow: hidden;
                }

                .emi-progress-fill {
                    height: 100%;
                    border-radius: 99px;
                    background: linear-gradient(90deg, #2563eb, #7b5ea7);
                    transition: width 0.8s ease;
                }

                /* Placeholder state */
                .emi-placeholder {
                    display: flex;
                    flex-direction: column;
                    align-items: center;
                    justify-content: center;
                    height: 100%;
                    min-height: 320px;
                    text-align: center;
                    color: #aaa;
                }

                .emi-placeholder-icon {
                    width: 72px;
                    height: 72px;
                    background: #f0ecf8;
                    border-radius: 50%;
                    display: flex;
                    align-items: center;
                    justify-content: center;
                    margin: 0 auto 18px;
                    font-size: 32px;
                }

                .emi-placeholder h5 {
                    color: #ccc;
                    font-weight: 700;
                    margin-bottom: 8px;
                }

                .emi-placeholder p {
                    font-size: 13px;
                    color: #bbb;
                    max-width: 220px;
                    margin: 0 auto;
                }

                @media (max-width: 768px) {
                    .emi-card { padding: 24px 18px; }
                    .emi-result-value { font-size: 32px; }
                    .emi-breakdown-row { flex-direction: column; }
                    .emi-result-card-col { margin-top: 24px; }
                }
            `}</style>

            <section className="emi-section">
                <div className="container">
                    {/* Section Title */}
                    <div className="row">
                        <div className="col-lg-12">
                            <div className="section-title" style={{ marginBottom: '40px' }}>
                                <h2>EMI Calculator</h2>
                                <p>Plan your loan repayment easily — enter your details to calculate your monthly EMI instantly.</p>
                            </div>
                        </div>
                    </div>

                    <div className="row align-items-stretch">
                        {/* ===== LEFT: FORM ===== */}
                        <div className="col-lg-6 col-md-12">
                            <div className="emi-card" style={{ height: '100%' }}>
                                <h4 style={{ color: '#0d1b4b', fontWeight: '800', marginBottom: '28px', fontSize: '20px' }}>
                                    Loan Details
                                </h4>
                                <form onSubmit={calculateEMI}>
                                    <div style={{ marginBottom: '20px' }}>
                                        <label className="emi-label">Loan Amount (₹)</label>
                                        <input
                                            className="emi-input"
                                            type="number"
                                            placeholder="e.g. 500000"
                                            value={principal}
                                            onChange={e => setPrincipal(e.target.value)}
                                            min="1"
                                            required
                                        />
                                    </div>

                                    <div style={{ marginBottom: '20px' }}>
                                        <label className="emi-label">Annual Interest Rate (%)</label>
                                        <input
                                            className="emi-input"
                                            type="number"
                                            placeholder="e.g. 10.5"
                                            value={rate}
                                            step="0.1"
                                            min="0.1"
                                            onChange={e => setRate(e.target.value)}
                                            required
                                        />
                                    </div>

                                    <div style={{ marginBottom: '32px' }}>
                                        <label className="emi-label">Loan Tenure (Months)</label>
                                        <input
                                            className="emi-input"
                                            type="number"
                                            placeholder="e.g. 24"
                                            value={tenure}
                                            min="1"
                                            onChange={e => setTenure(e.target.value)}
                                            required
                                        />
                                    </div>

                                    <button type="submit" className="emi-calc-btn">
                                        Calculate EMI
                                    </button>

                                    {emi && (
                                        <button type="button" className="emi-reset-btn" onClick={handleReset}>
                                            Reset
                                        </button>
                                    )}
                                </form>
                            </div>
                        </div>

                        {/* ===== RIGHT: RESULT ===== */}
                        <div className="col-lg-6 col-md-12 emi-result-card-col">
                            <div className="emi-card" style={{ height: '100%' }}>
                                {emi ? (
                                    <>
                                        {/* Monthly EMI Header */}
                                        <div className="emi-result-header">
                                            <p className="emi-result-label">Monthly EMI</p>
                                            <h2 className="emi-result-value">₹{formatINR(emi)}</h2>
                                            <p className="emi-result-sub">for {tenure} months</p>
                                        </div>

                                        {/* Principal + Interest cards */}
                                        <div className="emi-breakdown-row">
                                            <div className="emi-breakdown-card principal">
                                                <p className="emi-breakdown-label">Principal Amount</p>
                                                <h5 className="emi-breakdown-amount principal">₹{formatINR(principal)}</h5>
                                            </div>
                                            <div className="emi-breakdown-card interest">
                                                <p className="emi-breakdown-label">Total Interest</p>
                                                <h5 className="emi-breakdown-amount interest">₹{formatINR(totalInterest)}</h5>
                                            </div>
                                        </div>

                                        {/* Total Payment */}
                                        <div className="emi-total-row">
                                            <span className="emi-total-label">Total Payment</span>
                                            <h4 className="emi-total-amount">₹{formatINR(totalPayment)}</h4>
                                        </div>

                                        {/* Progress bar */}
                                        <div className="emi-progress-wrap">
                                            <div className="emi-progress-label">
                                                <span style={{ color: '#2563eb' }}>Principal {principalPercent}%</span>
                                                <span style={{ color: '#f97316' }}>Interest {100 - principalPercent}%</span>
                                            </div>
                                            <div className="emi-progress-bar">
                                                <div
                                                    className="emi-progress-fill"
                                                    style={{ width: `${principalPercent}%` }}
                                                />
                                            </div>
                                        </div>
                                    </>
                                ) : (
                                    <div className="emi-placeholder">
                                        <div className="emi-placeholder-icon">🧮</div>
                                        <h5>Your EMI will appear here</h5>
                                        <p>Fill in your loan amount, interest rate, and tenure to get instant results.</p>
                                    </div>
                                )}
                            </div>
                        </div>
                    </div>
                </div>
            </section>
        </>
    );
}

export default EmiCalculator;